import {
  Button,
  chakra,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent, ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast
} from "@chakra-ui/react";
import { FormInput, FormSelect } from "../../../../components/Forms";
import * as React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useGetPaginatedSuppliersWithTiers } from "../../../../hooks/query/supplier-administration";
import { showErrorToast, sortAlphabeticallyByAttribute } from "../../../../utils";
import { useState } from "react";
import { useGetAllSupplierCategories } from "../../../../hooks/query";

function SupplierCompanyModal({ modalData, isOpen, onClose, onSubmit }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierManagement.supplierCompanies.modal"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();

  const [tiers, setTiers] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    defaultValues: modalData
  });

  const tierValue = watch("tier");

  const {
    isFetching: isFetchingSuppliers
  } = useGetPaginatedSuppliersWithTiers(tierValue && tierValue > 1 ? `tier=${tierValue - 1}&size=200&sort=supplier.name` : "",
    {
      onSuccess: (data) => {
        setTiers(data?.content?.map(el => {
          return { id: el.id, name: el.name };
        }) ?? []);
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0
    }
  );

  const prepareSubmitData = data => {
    onSubmit({
      id: data.id,
      name: data.supplier,
      tier: data.tier,
      parentId: data.parent ? data.parent : null,
      category: data.category
    });
  };

  const tierOptions = [
    { label: `${t("labels.tier")} 1`, value: 1 },
    { label: `${t("labels.tier")} 2`, value: 2 },
    { label: `${t("labels.tier")} 3`, value: 3 },
    { label: `${t("labels.tier")} 4`, value: 4 }
  ];

  const generateParentOptions = () => {
    if (tierValue > 1) {
      return tiers
        .filter(el => el.id !== modalData?.id)
        .map(el => {
          return <option key={`tier-${el.id}`} value={el.id}>{el.name}</option>;
        });
    } else {
      return [];
    }
  };

  const tierSelector = register("tier", {
    required: globalTranslation("entityRequired", { entityName: t("labels.tier") })
  });

  const {
    data: categories,
    isFetching: isFetchingCategories
  } = useGetAllSupplierCategories({
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0
  });

  return isOpen && !isFetchingSuppliers && !isFetchingCategories ? <Modal isOpen={isOpen} onClose={onClose} size="md">
    <ModalOverlay />
    <ModalContent>
      <chakra.form onSubmit={handleSubmit(prepareSubmitData)}>
        <ModalHeader>{modalData ? t("updateSupplierCompanyHeader") : t("createSupplierCompanyHeader")}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Flex direction="column" gap="2" marginBottom={10}>
            <FormInput
              label={`${t("labels.supplierName")} *`}
              errors={errors}
              {...register("supplier", {
                required: globalTranslation("entityRequired", { entityName: t("labels.supplierName") })
              })}
              maxInlineSize={"var(--chakra-sizes-full)"}
            />
            <FormSelect
              {...register("category")}
              label={`${t("labels.supplierCategory")}`}
              placeholder={" "}
              errors={errors}
              maxInlineSize={"var(--chakra-sizes-full)"}
            >
              {categories
                ?.sort((a, b) => sortAlphabeticallyByAttribute(a, b, "name"))
                ?.map(category => <option key={category?.id} value={category?.name}>{category?.name}</option>) ?? []
              }
            </FormSelect>
            <FormSelect
              label={`${t("labels.tier")} *`}
              placeholder={" "}
              errors={errors}
              {...tierSelector}
              maxInlineSize={"var(--chakra-sizes-full)"}
              onChange={(e) => {
                tierSelector.onChange(e);
                setValue("parent", null, { shouldDirty: true });
              }}
            >
              {tierOptions.map(tier =>
                <option key={tier.label} value={tier.value}>{tier.label}</option>
              )}
            </FormSelect>
            {tierValue && tierValue > 1 ? <FormSelect
              label={`${t("labels.parent")} ${tierValue && tierValue > 1 ? "*" : ""}`}
              placeholder={" "}
              errors={errors}
              {...register("parent", {
                required: tierValue && tierValue > 1 ?
                  globalTranslation("entityRequired", { entityName: t("labels.parent") }) : false
              })}
              maxInlineSize={"var(--chakra-sizes-full)"}
            >
              {generateParentOptions()}
            </FormSelect> : null}
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t("buttons.close")}
          </Button>
          <Button colorScheme="customScheme" type="submit">
            {t("buttons.save")}
          </Button>
        </ModalFooter>
      </chakra.form>
    </ModalContent>
  </Modal> : null;
}

export default SupplierCompanyModal;